<template>
  <div class="advanced-incidents-management">
    <!-- 📋 Header Principal -->
    <div class="incidents-header">
      <div class="header-content">
        <div class="header-left">
          <v-icon size="40" color="white" class="mr-4">mdi-alert-circle</v-icon>
          <div>
            <h1 class="incidents-title">Gestión Avanzada de Incidencias MGI</h1>
            <p class="incidents-subtitle">
              Sistema integral de gestión, seguimiento y resolución de incidencias empresariales
            </p>
          </div>
        </div>
        
        <div class="header-actions">
          <!-- P13-V7B: offered only where the route authority admits the session. `/maps` admits
               super_admin alone, so a department head who pressed this was sent to /access-denied.
               The question is asked of the router itself, not of a role list kept here. -->
          <v-btn
            v-if="admits('/maps')"
            color="white"
            variant="outlined"
            prepend-icon="mdi-map"
            class="mr-3"
            data-testid="incidents-view-on-map"
            @click="viewOnMap"
          >
            Ver en Mapa
          </v-btn>
          <v-btn
            color="white"
            variant="elevated"
            prepend-icon="mdi-plus"
            size="large"
            @click="openCreateDialog"
            v-if="auth.userRole !== 'department_head'"  
          >
            Nueva Incidencia
          </v-btn>
        </div>
      </div>
    </div>

    <!-- 📊 Dashboard de Estadísticas -->
    <!--
      P13-V7B — FOUR COUNTS, AND NOTHING THAT WAS NOT COUNTED.
      Each card carried a trend line — "+2 hoy", "-1 hoy", "Sin cambios", "+8 hoy" — written into the
      template as text: no request, no computation, identical for every user on every day. They are
      removed. The fourth card was labelled "Resueltas Hoy" while counting CLOSED incidences of any
      date; it now says what it counts. A figure nobody could establish renders as an absence.
    -->
    <div class="stats-dashboard" data-testid="incidents-stat-cards">
      <v-row>
        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card critical-card" elevation="3">
            <v-card-text>
              <div class="stat-content">
                <div class="stat-icon">
                  <v-icon size="32" color="white">mdi-alert-circle</v-icon>
                </div>
                <div class="stat-numbers">
                  <div class="stat-value">{{ shownStat(stats.critical) }}</div>
                  <div class="stat-label">Críticas</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card warning-card" elevation="3">
            <v-card-text>
              <div class="stat-content">
                <div class="stat-icon">
                  <v-icon size="32" color="white">mdi-alert</v-icon>
                </div>
                <div class="stat-numbers">
                  <div class="stat-value">{{ shownStat(stats.high) }}</div>
                  <div class="stat-label">Alta Prioridad</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card info-card" elevation="3">
            <v-card-text>
              <div class="stat-content">
                <div class="stat-icon">
                  <v-icon size="32" color="white">mdi-information</v-icon>
                </div>
                <div class="stat-numbers">
                  <div class="stat-value">{{ shownStat(stats.medium) }}</div>
                  <div class="stat-label">Media Prioridad</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="stat-card success-card" elevation="3">
            <v-card-text>
              <div class="stat-content">
                <div class="stat-icon">
                  <v-icon size="32" color="white">mdi-check-circle</v-icon>
                </div>
                <div class="stat-numbers">
                  <div class="stat-value">{{ shownStat(stats.resolved) }}</div>
                  <div class="stat-label">Cerradas</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- 🔍 Panel de Filtros Avanzados -->
    <v-card class="filters-panel" elevation="2">
      <v-card-title class="filter-header">
        <v-icon class="mr-2">mdi-filter-variant</v-icon>
        Filtros Avanzados
        <v-spacer></v-spacer>
        <!-- Selector multi-departamento visible solo para Jefe de Departamento con >1 membresía -->
        <div v-if="auth.userRole === 'department_head' && accessibleDepartments.length > 1" class="mr-4" style="min-width:260px;">
          <v-select
            v-model="selectedDepartmentId"
            :items="departmentSelectOptions"
            label="Departamento"
            variant="outlined"
            density="compact"
            item-title="title"
            item-value="value"
            :loading="departmentsLoading"
            @update:model-value="handleDepartmentChange"
          />
        </div>
        <v-btn
          icon="mdi-refresh"
          variant="text"
          size="small"
          @click="refreshData"
          :loading="loading"
        ></v-btn>
      </v-card-title>
      
      <v-card-text>
        <v-row>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="filters.search"
              label="Buscar incidencia..."
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              density="compact"
              clearable
              @keyup.enter="applyFilters"
            ></v-text-field>
          </v-col>
          
          <v-col cols="12" md="2">
            <v-select
              v-model="filters.priority"
              :items="priorityOptions"
              label="Prioridad"
              variant="outlined"
              density="compact"
              clearable
              multiple
              chips
            ></v-select>
          </v-col>

          <v-col cols="12" md="2">
            <v-select
              v-model="filters.status"
              :items="statusOptions"
              label="Estado"
              variant="outlined"
              density="compact"
              clearable
              multiple
              chips
            ></v-select>
          </v-col>

          <!-- P13-V7B (refutation): the "Categoría" filter is RETIRED. An incidence has no `category`
               column (models/Incidence.js carries `classificationId`), so the super-admin rows never
               carry one and the department reader's rows all arrived as an invented 'OTHER': picking
               any category emptied the table, or matched every row. A filter over a field nobody sends
               is a promise, not a filter. -->

          <!-- P13-V7B (refutation): offered only where the rows carry what it filters. It matches
               `reportedBy.id`, and only the department reader maps `reportedByUser` into that object;
               the super-admin list carries `reportedBy` as a bare user id, so every pick emptied the
               table. It filters by REPORTER, and now says so ("Supervisor" named a role it never read). -->
          <v-col v-if="isDepartmentHeadSession" cols="12" md="2">
            <v-select
              v-model="filters.supervisor"
              :items="supervisorOptions"
              label="Reportado por"
              variant="outlined"
              density="compact"
              clearable
              multiple
              chips
              data-testid="incidents-reporter-filter"
            ></v-select>
          </v-col>

          <v-col cols="12" md="1" class="d-flex align-center">
            <v-btn
              color="primary"
              block
              @click="applyFilters"
              :loading="filtering"
            >
              Filtrar
            </v-btn>
          </v-col>
        </v-row>
        
        <!--
          P13-V7B (refutation round 3): the "Fecha desde / hasta" controls are RETIRED. The list reader has
          no date filter and the page applied none either: they filtered nothing while looking as if
          they did.
        -->
        <v-row class="mt-2">
          <v-col cols="12" v-if="multiValueFilterOnPage" data-testid="incidents-page-filter-note">
            <v-alert type="info" variant="tonal" density="compact">
              Con varios valores de estado o prioridad, el filtro se aplica sólo a la página visible.
            </v-alert>
          </v-col>

          <!--
            XSC-M3A · el filtro de agencia solo se anuncia donde existe una autoridad de catálogo
            válida para el rol. Para department_head no se renderiza en absoluto: un selector vacío
            o deshabilitado seguiría ofreciendo una capacidad que el piloto firmado no vende.
          -->
          <v-col cols="12" md="3" v-if="agencyCatalogueSurfaceAllowed">
            <v-select
              v-model="filters.agency"
              :items="agencyOptions"
              label="Agencia"
              variant="outlined"
              density="compact"
              clearable
            ></v-select>
          </v-col>

          <v-col cols="12" md="3" class="d-flex align-center gap-2">
            <v-btn
              variant="outlined"
              prepend-icon="mdi-filter-remove"
              @click="clearFilters"
              size="small"
            >
              Limpiar
            </v-btn>
            <!-- P13-V7B: the "Exportar" button is RETIRED. It answered "Función de exportación en
                 desarrollo" and exported nothing; there is no export producer behind this page. -->
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 📋 Tabla Avanzada de Incidencias -->
    <v-card class="incidents-table-card" elevation="2">
      <v-card-title class="table-header">
        <v-icon class="mr-2">mdi-format-list-bulleted</v-icon>
        <span data-testid="incidents-count-header">Incidencias ({{ filteredIncidents.length }}<template v-if="totalIncidents !== null"> de {{ totalIncidents }}</template>)</span>
        <v-spacer></v-spacer>
        <div class="table-actions">
          <v-btn-toggle v-model="viewMode" mandatory>
            <v-btn value="table" icon="mdi-view-list" size="small"></v-btn>
            <v-btn value="cards" icon="mdi-view-grid" size="small"></v-btn>
          </v-btn-toggle>
        </div>
      </v-card-title>
      
      <!-- Debug info -->
      <!-- <v-card-subtitle class="pa-4">
        🐛 Debug: incidents={{ incidents.length }}, filtered={{ filteredIncidents.length }}, loading={{ loading }}
      </v-card-subtitle> -->
      
      <!-- Vista de tabla -->
      
      <!-- ✅ TABLA HTML SIMPLE - FUNCIONAL -->
      <div v-if="viewMode === 'table'" :key="forceRender" class="pa-4">
        <!-- P13-V7B (refutation): the green "✅ Tabla Funcional: Mostrando N incidencias" banner is
             RETIRED. It was a developer's debug note shown to every user as a success alert; the count
             it repeated is already in the header, which states the server's total. -->

        <v-table :key="`table-${forceRender}`" class="incidents-simple-table">
          <thead>
            <tr>
              <th class="text-left">ID</th>
              <th class="text-left">Título</th>
              <th class="text-left">Prioridad</th>
              <th class="text-left">Estado</th>
              <th class="text-left">Categoría</th>
              <th class="text-left">Asignado a</th>
              <th class="text-left">Fecha</th>
              <th class="text-left">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in filteredIncidents" :key="item.id" class="hover-row">
              <td>{{ item.id }}</td>
              <td class="font-weight-medium">{{ item.title }}</td>
              <td>
                <!-- P13-V7B (refutation): an absent priority is shown as absent, not as a blank chip. -->
                <v-chip
                  v-if="item.priority"
                  :color="item.priority === 'HIGH' ? 'orange' : item.priority === 'CRITICAL' ? 'red' : 'blue'"
                  size="small"
                >
                  {{ getPriorityText(item.priority) }}
                </v-chip>
                <span v-else class="text-grey">Sin prioridad</span>
              </td>
              <td>
                <v-chip
                  :color="getStatusColor(item.status)"
                  size="small"
                >
                  {{ getStatusText(item.status) }}
                </v-chip>
              </td>
              <td>
                <!-- P13-V7B (refutation): no reader sends a category (see the retired filter), so the
                     cell used to print an empty chip or the invented "Otro". The absence is stated. -->
                <v-chip
                  v-if="item.category"
                  color="grey"
                  size="small"
                  variant="outlined"
                >
                  {{ getCategoryText(item.category) }}
                </v-chip>
                <span v-else class="text-grey">Sin categoría</span>
              </td>
              <td>
                <div v-if="item.assignedTo?.name" class="d-flex align-center">
                  <v-avatar size="28" class="mr-2">
                    <v-img :src="item.assignedTo?.avatar || '/default-avatar.png'"></v-img>
                  </v-avatar>
                  <span class="text-body-2">{{ item.assignedTo?.name }}</span>
                </div>
                <div v-else-if="auth.userRole === 'department_head' && (item as any).reportedBy" class="d-flex align-center">
                  <v-avatar size="28" class="mr-2">
                    <v-icon>mdi-account</v-icon>
                  </v-avatar>
                  <span class="text-body-2">{{ (item as any).reportedBy?.name }}</span>
                </div>
                <v-chip v-else color="grey" size="small" variant="outlined">
                  Sin asignar
                </v-chip>
              </td>
              <td>
                <div class="text-body-2" v-if="item.createdAt">
                  {{ new Date(item.createdAt).toLocaleDateString() }}
                  <div class="text-caption text-grey">
                    {{ new Date(item.createdAt).toLocaleTimeString() }}
                  </div>
                </div>
                <span v-else class="text-grey">Sin fecha</span>
              </td>
              <td>
                <div class="actions-cell">
                  <v-btn
                    icon="mdi-eye"
                    size="small"
                    variant="text"
                    title="Ver detalles"
                    @click="viewIncident(item)"
                  ></v-btn>
                  <!-- W2 LIFECYCLE-SIBLING: the generic edit is not a sold first-pilot capability. -->
                  <v-btn
                    v-if="canUseLifecycleWrites"
                    icon="mdi-pencil"
                    size="small"
                    variant="text"
                    title="Editar"
                    @click="editIncident(item)"
                  ></v-btn>
                  <!-- P13-V7B (refutation): offered only where assign-user is admitted (see
                       `canAssignHandler`); a vendor who pressed it read a 403 from eligible-assignees. -->
                  <v-btn
                    v-if="canAssignHandler"
                    icon="mdi-account-arrow-right"
                    size="small"
                    variant="text"
                    title="Asignar"
                    data-testid="incidents-row-assign"
                    @click="assignIncident(item)"
                  ></v-btn>
                  <v-btn
                    icon="mdi-comment-plus"
                    size="small"
                    variant="text"
                    title="Agregar comentario"
                    @click="addComment(item)"
                  ></v-btn>
                  <!-- W2 LIFECYCLE-SIBLING: the generic delete is not a sold first-pilot capability. -->
                  <v-btn
                    v-if="canUseLifecycleWrites"
                    icon="mdi-delete"
                    size="small"
                    variant="text"
                    color="error"
                    title="Eliminar"
                    @click="deleteIncident(item)"
                  ></v-btn>
                </div>
              </td>
            </tr>
          </tbody>
        </v-table>
        
        <!-- Mensaje si no hay datos -->
        <div v-if="filteredIncidents.length === 0 && !loading" class="text-center pa-8">
          <v-icon size="64" color="grey-lighten-2">mdi-alert-circle-outline</v-icon>
          <h3 class="text-h6 mt-4 text-grey">No se encontraron incidencias</h3>
          <p class="text-grey" v-if="auth.userRole !== 'department_head'">Intenta ajustar los filtros o crear una nueva incidencia</p>
          <p class="text-grey" v-else>
            No hay incidencias visibles para el departamento seleccionado.
            <span v-if="accessibleDepartments.length > 1">Prueba cambiar el departamento en el selector superior.</span>
          </p>
        </div>

        <!-- Paginación real en backend, para todos los lectores (P13-V7B refutation) -->
        <div class="d-flex align-center justify-end pa-2" data-testid="incidents-pager">
          <v-select
            :items="[10, 25, 50, 100]"
            v-model="itemsPerPage"
            label="Items por página"
            density="compact"
            style="max-width: 160px"
          />
          <v-pagination
            v-model="currentPage"
            :length="Math.max(1, Math.ceil((totalIncidents || 0) / itemsPerPage))"
            density="comfortable"
            class="ml-4"
          />
        </div>
      </div>
      
      <!-- ❌ TABLA VUETIFY (PROBLEMÁTICA - OCULTA TEMPORALMENTE) -->
      <div v-if="false && viewMode === 'table'" style="display: none;">
      <v-data-table
        v-if="viewMode === 'table'"
        :headers="tableHeaders"
        :items="filteredIncidents"
        :loading="loading"
        :items-per-page="itemsPerPage"
        :sort-by="[{ key: 'createdAt', order: 'desc' }]"
        item-value="id"
        class="incidents-table"
        show-select
        v-model="selectedIncidents"
      >
        <!-- Header personalizado con acciones en lote -->
        <template v-slot:top>
          <div v-if="selectedIncidents.length > 0" class="bulk-actions">
            <v-alert
              type="info"
              variant="tonal"
              density="compact"
              class="ma-4"
            >
              <div class="d-flex align-center">
                <span>{{ selectedIncidents.length }} incidencias seleccionadas</span>
                <v-spacer></v-spacer>
                <!-- P13-V7B (refutation): the bulk "Asignar" and "Cambiar Estado" are RETIRED. Both
                     handlers only toasted "Función … en desarrollo": no bulk writer exists, and the
                     single writers they would stand for refuse the vendor (assign-user) or need a
                     comment, a resolution or a claim this bar never collects (PUT /:id/status). -->
                <v-btn
                  variant="outlined"
                  size="small"
                  prepend-icon="mdi-delete"
                  color="error"
                  @click="bulkDelete"
                >
                  Eliminar
                </v-btn>
              </div>
            </v-alert>
          </div>
        </template>

        <!-- Columna de prioridad -->
        <template v-slot:item.priority="{ item }">
          <v-chip
            :color="getPriorityColor(item.priority)"
            size="small"
            :prepend-icon="getPriorityIcon(item.priority)"
            variant="flat"
          >
            {{ item.priority }}
          </v-chip>
        </template>

        <!-- Columna de estado -->
        <template v-slot:item.status="{ item }">
          <v-chip
            :color="getStatusColor(item.status)"
            size="small"
            variant="flat"
            @click="showStatusHistory(item)"
            style="cursor: pointer"
          >
            {{ getStatusText(item.status) }}
          </v-chip>
        </template>

        <!-- Columna de categoría -->
        <template v-slot:item.category="{ item }">
          <v-chip
            :color="getCategoryColor(item.category)"
            size="small"
            variant="outlined"
          >
            {{ item.category }}
          </v-chip>
        </template>

        <!-- Columna de supervisor asignado -->
        <template v-slot:item.assignedTo="{ item }">
          <div v-if="item.assignedTo?.name" class="d-flex align-center">
            <v-avatar size="24" class="mr-2">
              <v-img :src="item.assignedTo?.avatar || '/default-avatar.png'"></v-img>
            </v-avatar>
            <span class="text-body-2">{{ item.assignedTo?.name }}</span>
          </div>
          <v-chip v-else color="grey" size="small" variant="outlined">
            Sin asignar
          </v-chip>
        </template>

        <!-- Columna de fecha de creación -->
        <template v-slot:item.createdAt="{ item }">
          <div class="text-body-2" v-if="item.createdAt">
            {{ formatDate(item.createdAt) }}
            <div class="text-caption text-grey">
              {{ getTimeAgo(item.createdAt) }}
            </div>
          </div>
          <span v-else class="text-grey">Sin fecha</span>
        </template>

        <!-- Columna de acciones -->
        <template v-slot:item.actions="{ item }">
          <div class="actions-cell">
            <v-btn
              icon="mdi-eye"
              size="small"
              variant="text"
              @click="viewIncident(item)"
              title="Ver detalles"
            ></v-btn>
            <v-btn
              icon="mdi-pencil"
              size="small"
              variant="text"
              @click="editIncident(item)"
              title="Editar"
            ></v-btn>
            <v-btn
              v-if="canAssignHandler"
              icon="mdi-account-arrow-right"
              size="small"
              variant="text"
              @click="assignIncident(item)"
              title="Asignar"
            ></v-btn>
            <v-btn
              icon="mdi-comment-plus"
              size="small"
              variant="text"
              @click="addComment(item)"
              title="Agregar comentario"
            ></v-btn>
            <v-btn
              icon="mdi-delete"
              size="small"
              variant="text"
              color="error"
              @click="deleteIncident(item)"
              title="Eliminar"
            ></v-btn>
          </div>
        </template>
      </v-data-table>
      </div> <!-- Cierre del div v-if="false && viewMode === 'table'" -->

      <!-- Vista de tarjetas -->
      <div v-if="viewMode === 'cards'" class="cards-view pa-4">
        <v-row>
          <v-col
            v-for="incident in filteredIncidents"
            :key="incident.id"
            cols="12"
            md="6"
            lg="4"
          >
            <IncidentCard
              :incident="incident"
              :can-use-lifecycle-writes="canUseLifecycleWrites"
              :can-assign="canAssignHandler"
              @view="viewIncident"
              @edit="editIncident"
              @delete="deleteIncident"
              @assign="assignIncident"
              @comment="addComment"
            />
          </v-col>
        </v-row>
      </div>
    </v-card>

    <!-- 📝 Diálogo de Crear/Editar Incidencia -->
    <IncidentFormDialog
      v-model="dialogs.form"
      :incident="selectedIncident"
      :agencies="agencies"
      @save="handleSaveIncident"
      @cancel="handleCancelForm"
    />

    <!-- 👁️ Diálogo de Detalles de Incidencia -->
    <!-- P13-V7B (refutation): `can-assign` and `can-change-status` are no longer forced true.
         "Asignar" is declared only where assign-user is admitted (`canAssignHandler`). "Cambiar
         Estado" is not declared at all: it offered five fixed targets and sent `{ status }` alone, while
         the state machine needs a claim (start), a comment (cancel, return, reopen), a resolution
         (submit) or the agency employee (validate) — no target could succeed from this menu. -->
    <IncidentDetailsDialog
      ref="detailsDialogRef"
      v-model="dialogs.details"
      :can-use-lifecycle-writes="canUseLifecycleWrites"
      :can-comment="true"
      :can-assign="canAssignHandler"
      :can-view-history="true"
      :incident="selectedIncident"
      :supervisors="supervisors"
      :agencies="agencies"
      @edit="editIncident"
      @assign="assignIncident"
      @comment="addComment"
      @status-change="handleStatusChange"
      @view-history="showStatusHistory"
      @incident-updated="handleIncidentUpdated"
      @close="handleCloseDetails"
    />

    <!-- 👤 Diálogo de Asignación (P13-V1C: assign-user canónico, lista advisory del servidor) -->
    <AssignIncidentDialog
      v-model="dialogs.assign"
      :incident="selectedIncident"
      :assignees="eligibleAssignees"
      :loading="assigneesLoading"
      @assign="handleAssignIncident"
      @cancel="handleCancelAssign"
    />

    <!-- 💬 Diálogo de Comentarios -->
    <CommentsDialog
      v-model="dialogs.comments"
      :incident="selectedIncident"
      @add-comment="handleAddComment"
      @close="handleCloseComments"
    />

    <!-- 🗑️ Diálogo de Confirmación de Eliminación -->
    <ConfirmDeleteDialog
      v-model="dialogs.delete"
      :incident="selectedIncident"
      :multiple="selectedIncidents.length > 1"
      :request-pending="deleteRequestPending"
      @confirm="handleDeleteConfirm"
      @cancel="handleCancelDelete"
    />

    <!-- 📊 Diálogo de Historial de Estados -->
    <StatusHistoryDialog
      v-model="dialogs.history"
      :incident="selectedIncident"
      @close="handleCloseHistory"
    />

    <!-- 🔔 Snackbar de Notificaciones -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
      location="top right"
      elevation="8"
    >
      <div class="d-flex align-center">
        <v-icon class="mr-2" :icon="snackbar.icon" />
        <span>{{ snackbar.message }}</span>
      </div>
      <template #actions>
        <v-btn
          variant="text"
          icon="mdi-close"
          size="small"
          @click="snackbar.show = false"
        />
      </template>
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
// 🚩 Ajuste: Para Jefe de Departamento, limitar incidencias a su departamento usando endpoint real
// Seguridad: el backend ya excluye DepartmentHead del listado general (/api/incidences)
// Escalabilidad: el endpoint departamental soporta paginación y filtros server-side
import { ref, onMounted, onUnmounted, computed, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { incidentsService } from '../../services/incidentsService'
import { useAuthStore } from '@/stores/auth'
import { useRoute } from 'vue-router'
// P13-V7B: what this page may OFFER is asked of the route table, under the guard's own role rule.
import { useRouteAdmission } from '@/composables/useRouteAdmission'
import JefeDepartamentoService from '@/services/roles/JefeDepartamentoService.js'
import type { Incident, CreateIncidentRequest, UpdateIncidentRequest, Agency, Supervisor, EligibleAssignee } from '../../types/incidents'
// P13-V6B@V1 — consumo canónico de señales realtime del dominio (refetch server-scoped; el payload
// nunca materializa estado ni autoriza nada) + invalidación mínima de audiencia previa.
import websocketService from '@/services/websocket.service'
import { RT_LOCAL } from '@/services/realtime-events'
import { useTrailingRefetch } from '@/composables/useTrailingRefetch'

// Función para mostrar notificaciones
const showNotification = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
  console.log(`${type.toUpperCase()}: ${message}`)
  
  // Mapeo de iconos por tipo
  const iconMap = {
    success: 'mdi-check-circle',
    error: 'mdi-alert-circle',
    warning: 'mdi-alert',
    info: 'mdi-information'
  }
  
  // Actualizar estado del snackbar
  snackbar.value = {
    show: true,
    message,
    color: type,
    timeout: type === 'error' ? 6000 : 4000, // Errores se muestran más tiempo
    icon: iconMap[type]
  }
}

// Importar componentes
import IncidentCard from './components/IncidentCard.vue'
// W2 LIFECYCLE-SIBLING (client truth): one shared mirror of the merged backend allowlist.
import { canUseLifecycleSiblingWrites } from '@/config/lifecycleSiblingPerimeter'
import IncidentFormDialog from './components/IncidentFormDialog.vue'
import IncidentDetailsDialog from './components/IncidentDetailsDialog.vue'
import AssignIncidentDialog from './components/AssignIncidentDialog.vue'
import CommentsDialog from './components/CommentsDialog.vue'
import ConfirmDeleteDialog from './components/ConfirmDeleteDialog.vue'
import StatusHistoryDialog from './components/StatusHistoryDialog.vue'

const router = useRouter()
const route = useRoute()
const auth = useAuthStore()
const { admits } = useRouteAdmission()

// Estado del componente
const loading = ref(false)
const filtering = ref(false)
const incidents = ref<Incident[]>([])
const selectedIncidents = ref<Incident[]>([])
const selectedIncident = ref<Incident | null>(null)
/**
 * P13-V7B — `null` means "the size of the result is not known", and it is a real state: before the
 * first read resolves, and whenever the department reader states no total. It used to start at 0 and
 * fall back to the number of rows received, so the header read "25 de 25" over a truncated page.
 */
const totalIncidents = ref<number | null>(null)
const detailsDialogRef = ref<InstanceType<typeof IncidentDetailsDialog> | null>(null)
/**
 * W2 LIFECYCLE-SIBLING client truth. `/incidents` admits `department_head` — a DELIVERED first-pilot
 * identity — alongside the vendor/administration roles, and this view rendered the generic
 * edit/delete affordances and the comment edit/delete menu to all of them. The merged backend
 * perimeter now refuses every delivered identity before any business layer, so showing the action
 * would be a false surface. The affordance AND the dispatch path are both gated: a hidden button is
 * surface truth, an early return is what makes the negative adversarial.
 */
const canUseLifecycleWrites = computed(() => canUseLifecycleSiblingWrites(auth.userRole))

/**
 * P13-V7B (refutation) — WHO MAY BE OFFERED "Asignar".
 *
 * The repair declared `:can-assign="true"` and the row buttons were unconditional, so the vendor was
 * offered an assignment it can never make: `GET /incidences/:id/eligible-assignees` and
 * `POST /incidences/:id/assign-user` both admit `department_head` ALONE (routes/incidences.js:481-494;
 * super_admin is release-only recovery there). `/incidents` admits super_admin and department_head,
 * so the rule is one comparison. The backend still decides every request; this only stops offering a
 * refusal.
 */
const isDepartmentHeadSession = computed(() => auth.userRole === 'department_head')
const canAssignHandler = isDepartmentHeadSession

const viewMode = ref<'table' | 'cards'>('table')
const itemsPerPage = ref(25)
const currentPage = ref(1)
const forceRender = ref(0) // Para forzar re-render

// Department scope (solo aplica a DepartmentHead).
//
// LA AUTORIDAD DE ALCANCE ES DEL SERVIDOR. `loadContext` (departmentHeadController) deriva los
// departamentos accesibles del actor desde DepartmentMembership; un `departmentId` del cliente sólo
// puede ESTRECHAR ese conjunto. Por eso `departmentId === null` NO es un error: significa "sin
// estrechamiento", y la lectura canónica se realiza igual.
const departmentId = ref<number | null>(null)
// Tercer estado, distinto de `null`: se suministró un departmentId y era inválido. Fail closed —
// jamás se degrada a la lectura sin alcance, que devolvería MÁS datos que los pedidos.
const departmentContextInvalid = ref(false)
// Multi-departamento
const accessibleDepartments = ref<any[]>([])
const selectedDepartmentId = ref<number | null>(null)
const departmentsLoading = ref(false)
const departmentSelectOptions = computed(() => accessibleDepartments.value.map(d => ({
  title: d.name || d.departmentName || `Departamento ${d.id}`,
  value: d.id || d.department_id || d.departmentId
})))

// Resolver un ESTRECHAMIENTO departamental opcional (ruta -> membresías -> backend).
// Devolver `null` ya no bloquea la lectura: el servidor decide el alcance.
const resolveDepartmentId = async (): Promise<number | null> => {
  // 1) Query param — contexto de compatibilidad, opcional y sólo de estrechamiento.
  //    ausente             → no hay nada que resolver aquí; el servidor decide.
  //    entero positivo     → estrecha.
  //    presente e inválido → FAIL CLOSED, y NO se continúa a las membresías: caer al camino de
  //                          "sin estrechamiento" convertiría una petición inválida en una lectura
  //                          MÁS amplia, que es exactamente lo que no debe pasar.
  //    "Presente" se decide por la PRESENCIA DE LA CLAVE, no por que su valor sea utilizable.
  //    `?departmentId=` (vacío), `?departmentId` (sin valor) y `?departmentId=1&departmentId=2`
  //    (ambiguo) son peticiones SUMINISTRADAS y malformadas: tratarlas como "ausente" las convertiría
  //    en la lectura sin alcance, que devuelve MÁS de lo que se pidió.
  //
  // `departmentContextInvalid` es DERIVADO de la query actual, nunca un pestillo: se recalcula en
  // cada resolución. Si fuera pegajoso, una sola URL malformada dejaría la vista muerta durante toda
  // la vida del componente aunque después llegara un departamento válido — over-denial del jefe de
  // departamento legítimo, que es un fallo tan grave como la fuga.
  departmentContextInvalid.value = false
  const routeQuery = (route.query ?? {}) as Record<string, unknown>
  const supplied = Object.prototype.hasOwnProperty.call(routeQuery, 'departmentId')
  if (supplied) {
    const raw = routeQuery.departmentId
    const n = typeof raw === 'string' && raw.trim() !== '' ? Number(raw.trim()) : Number.NaN
    if (!Number.isInteger(n) || n <= 0) {
      departmentContextInvalid.value = true
      departmentId.value = null
      selectedDepartmentId.value = null
      console.warn('⛔ departmentId suministrado e inválido; no se degrada a una lectura sin alcance:', raw)
      return null
    }
    departmentId.value = n
    selectedDepartmentId.value = departmentId.value
    return departmentId.value
  }

  // 2) AUSENTE ⇒ LIMPIAR EL ESTRECHAMIENTO ANTERIOR, EXPLÍCITAMENTE.
  //
  // No basta con "no resolver nada". El root monta un `<router-view />` SIN `key`, así que una
  // navegación que sólo cambia la query NO desmonta este componente. Si al desaparecer el parámetro
  // dejáramos en pie el `departmentId` de la navegación anterior, la lectura seguiría estrechada a un
  // departamento que la URL vigente ya no pide: estado viejo mandando sobre la ruta actual.
  departmentId.value = null
  selectedDepartmentId.value = null

  // 3) NO HAY SEGUNDA FUENTE, Y ESO ES LA REPARACIÓN.
  //
  // Aquí vivían dos intentos de DERIVAR el departamento desde las membresías del usuario
  // (`getUserMemberships(auth.user.id)` y, si fallaba, `getCurrentBackendUser()` + reintento), y de
  // ellos salía `memberships[0]`. Existían sólo para satisfacer la precondición que Option A elimina:
  // el servidor ya deriva el alcance del actor desde DepartmentMembership.
  //
  // Se retiran, no se dejan dormidos, por dos razones concretas:
  //  · Coste: eran cuatro peticiones condenadas (`/api/api/memberships/...`) en serie ANTES de poder
  //    leer las incidencias, cada una con el timeout de 10 s del cliente.
  //  · Riesgo: `GET /api/memberships/user/:id` SÍ admite `department_head` en el backend. El día que
  //    otro dueño repare ese prefijo, un jefe con varias membresías habría quedado estrechado en
  //    silencio a `memberships[0]` y habría dejado de ver el resto — exactamente el fallo que esta
  //    misión dice haber eliminado. Un estrechamiento implícito y no pedido no es alcance del
  //    servidor.
  //
  // El ÚNICO estrechamiento de cliente legítimo es explícito: el query param de arriba y el selector
  // de departamento (`handleDepartmentChange`).
  return null
}

// Estados de diálogos
const dialogs = ref({
  form: false,
  details: false,
  assign: false,
  comments: false,
  delete: false,
  history: false
})

// IDLE → REQUEST_PENDING → REQUEST_COMPLETED de la solicitud de eliminación. Vive en el padre, que
// es quien hace la petición: el diálogo se limita a reflejarlo.
const deleteRequestPending = ref(false)

// Estado del snackbar
const snackbar = ref({
  show: false,
  message: '',
  color: 'info',
  timeout: 4000,
  icon: 'mdi-information'
})

// Datos de soporte
const supervisors = ref<Supervisor[]>([])
// P13-V1C — lista ADVISORY de asignables (eligible-assignees); jamás autoridad local.
const eligibleAssignees = ref<EligibleAssignee[]>([])
const assigneesLoading = ref(false)
const agencies = ref<Agency[]>([])

// Filtros
const filters = ref({
  search: '',
  priority: [] as string[],
  status: [] as string[],
  category: [] as string[],
  supervisor: null as string | null,
  agency: null as string | null,
  dateFrom: '',
  dateTo: ''
})

// Estadísticas — P13-V1D: la semilla fabricada {critical:8, high:15, medium:23, resolved:42} se
// renderizaba como métrica real hasta el primer updateStats.
// P13-V7B: y el cero que la sustituyó también era una afirmación — cuatro ceros publicados antes
// de que ninguna lectura hubiera resuelto. `null` = todavía no se sabe; un cero real sigue siendo 0.
type StatFigure = number | null
const stats = ref<{ critical: StatFigure; high: StatFigure; medium: StatFigure; resolved: StatFigure }>({
  critical: null,
  high: null,
  medium: null,
  resolved: null
})

/** A stated figure renders as itself — zero included. Anything unknown is an explicit absence. */
const shownStat = (value: StatFigure): string => (value === null || value === undefined ? '—' : String(value))

// Opciones para selects
const priorityOptions = [
  { title: 'Crítica', value: 'CRITICAL' },
  { title: 'Alta', value: 'HIGH' },
  { title: 'Media', value: 'MEDIUM' },
  { title: 'Baja', value: 'LOW' }
]

// P13-V1D (frozen decision #1): los cinco estados canónicos — RESOLVED retirado,
// PENDING_VALIDATION visible y filtrable.
const statusOptions = [
  { title: 'Pendiente', value: 'PENDING' },
  { title: 'En Proceso', value: 'IN_PROGRESS' },
  { title: 'Pendiente Validación', value: 'PENDING_VALIDATION' },
  { title: 'Cerrado', value: 'CLOSED' },
  { title: 'Cancelado', value: 'CANCELLED' }
]

// P13-V7B (refutation): `categoryOptions` is RETIRED with the "Categoría" filter — see the template.

const supervisorOptions = computed(() => {
  // Para DepartmentHead derivamos dinámicamente de las incidencias (reportedBy)
  if (auth.userRole === 'department_head') {
    const map = new Map<string, { title: string; value: string }>()
    incidents.value.forEach(i => {
      const rep = (i as any).reportedBy
      if (rep?.id) {
        map.set(rep.id, { title: rep.name || `Supervisor ${rep.id}` , value: rep.id })
      }
    })
    return Array.from(map.values())
  }
  return supervisors.value.map(s => ({ title: s.name, value: s.id }))
})

/**
 * XSC-M3A · ¿puede esta vista exponer la superficie de catálogo de agencias para el rol actual?
 *
 * ALLOWLIST EXPLÍCITA, no una negación. `userRole !== 'department_head'` es fail-OPEN: `null`,
 * `undefined` y cualquier rol futuro pasarían por no ser el rol contenido, quedando autorizados por
 * omisión. Aquí solo pasan los roles nombrados; cualquier otro valor —incluido el ausente— cae del
 * lado cerrado.
 *
 * La lista describe el comportamiento de ESTA superficie, no una afirmación de autoridad de negocio:
 * son los roles que la ruta ya admite y que hoy consumen el catálogo, cuyo comportamiento esta misión
 * preserva sin tocarlo. Que `administration_manager` deba o no verlo es una pregunta de perímetro
 * aparte (`/points` es vendor-only, V7A), con su propio dueño; contenerla aquí sería ampliar el
 * alcance de esta reparación.
 */
const AGENCY_CATALOGUE_SURFACE_ROLES = ['super_admin', 'administration_manager']
const agencyCatalogueSurfaceAllowed = computed(() => AGENCY_CATALOGUE_SURFACE_ROLES.includes(String(auth.userRole ?? '')))

/**
 * XSC-M3A · limpieza en la TRANSICIÓN de autoridad, no en el siguiente request.
 *
 * `agencies` es un ref de instancia y la instancia sobrevive al cambio de rol: un catálogo obtenido
 * legítimamente como super_admin seguía en memoria cuando la MISMA página montada pasaba a
 * department_head. Ocultar el selector no vacía el estado, y esperar a que una petición posterior lo
 * sobrescriba deja una ventana en la que el catálogo contenido sigue vivo. Se limpia cuando la
 * superficie deja de estar permitida.
 */
watch(agencyCatalogueSurfaceAllowed, (allowed) => {
  if (allowed) return
  agencies.value = []
  filters.value.agency = null
}, { immediate: true })

// P13-V7B (refutation round 3): the list reader takes ONE status and ONE priority; several values are
// applied to the rows on screen only, and the page says so instead of implying a full-result filter.
const multiValueFilterOnPage = computed(() =>
  (filters.value.status?.length ?? 0) > 1 || (filters.value.priority?.length ?? 0) > 1
)

const agencyOptions = computed(() => {
  return agencies.value.map(a => ({
    title: a.name,
    value: a.id
  }))
})

// Headers de la tabla
const tableHeaders = [
  { title: 'ID', key: 'id', width: 80 },
  { title: 'Título', key: 'title', sortable: true },
  { title: 'Prioridad', key: 'priority', width: 120 },
  { title: 'Estado', key: 'status', width: 120 },
  { title: 'Categoría', key: 'category', width: 130 },
  { title: 'Asignado a', key: 'assignedTo', width: 150 },
  { title: 'Fecha', key: 'createdAt', width: 140 },
  { title: 'Acciones', key: 'actions', width: 200, sortable: false }
]

// Computed
const filteredIncidents = computed(() => {
  let filtered = incidents.value
  
  console.log('🔍 filteredIncidents computed - Iniciando con:', filtered.length, 'incidencias')

  if (filters.value.search) {
    const search = filters.value.search.toLowerCase()
    filtered = filtered.filter(incident =>
      incident.title.toLowerCase().includes(search) ||
      incident.description.toLowerCase().includes(search)
    )
    console.log('🔍 Después de filtro de búsqueda:', filtered.length, 'incidencias')
  }

  if (filters.value.priority.length > 0) {
    filtered = filtered.filter(incident =>
      filters.value.priority.includes(incident.priority)
    )
    console.log('🔍 Después de filtro de prioridad:', filtered.length, 'incidencias')
  }

  if (filters.value.status.length > 0) {
    filtered = filtered.filter(incident =>
      filters.value.status.includes(incident.status)
    )
    console.log('🔍 Después de filtro de estado:', filtered.length, 'incidencias')
  }

  if (filters.value.category.length > 0) {
    filtered = filtered.filter(incident =>
      filters.value.category.includes(incident.category)
    )
    console.log('🔍 Después de filtro de categoría:', filtered.length, 'incidencias')
  }

  // Filtro por supervisor (reportado por) – solo si se selecciona uno
  if (filters.value.supervisor && filters.value.supervisor.length > 0) {
    const supSet = new Set(filters.value.supervisor)
    filtered = filtered.filter(incident => supSet.has((incident as any).reportedBy?.id))
    console.log('🔍 Después de filtro de supervisor (multi):', filtered.length, 'incidencias')
  }

  console.log('✅ filteredIncidents computed - Resultado final:', filtered.length, 'incidencias')
  return filtered
})

// Generación de carga. `loadIncidents` se invoca desde muchos disparadores que pueden solaparse
// (refresco, filtros, paginación, cambio de departamento, refetch de realtime) y publicaba su
// resultado incondicionalmente, así que la respuesta que llegaba ÚLTIMA ganaba, no la más reciente.
// Con alcance por departamento eso no es sólo un parpadeo: una respuesta en vuelo del departamento
// anterior podía repintar la tabla DESPUÉS de un fail-closed, dejando filas visibles mientras el
// estado del componente dice que la petición fue rechazada. Sólo la última carga puede publicar.
const loadGeneration = ref(0)

// Métodos principales
const loadIncidents = async () => {
  const generation = ++loadGeneration.value
  const isStale = () => generation !== loadGeneration.value
  loading.value = true
  try {
    console.log('🔄 AdvancedIncidentsManagement.loadIncidents - Iniciando carga...')

    let response: { data: any[]; total?: number | null }
    // P13-V7B: the department reader's own statistics block, when this read carried one.
    let departmentMeta: any = null
    const isDepartmentHead = auth.userRole === 'department_head'
    // Si el rol es DepartmentHead, usar endpoint departamental real
  if (auth.userRole === 'department_head') {
      // SINCRONIZAR CON LA RUTA VIGENTE, SIEMPRE, ANTES DE CADA LECTURA FIRMADA.
      //
      // Antes esto era una precondición ("si no hay departmentId, no leas"), y como su único
      // productor era una lectura rota, la capacidad firmada quedaba muerta por construcción.
      // Después fue condicional (`!departmentId.value || inválido`), lo que dejaba obsoleto el
      // estrechamiento en cuanto una query VÁLIDA lo había fijado: el `<router-view />` del root no
      // lleva `key`, así que un cambio sólo-de-query no remonta y el componente seguía leyendo con el
      // departamento de la navegación anterior.
      //
      // `resolveDepartmentId()` es idempotente y deriva TODO su resultado de la query actual —
      // ausente, válida o malformada— así que la forma correcta es llamarla incondicionalmente. La
      // ruta vigente manda; el estado previo del componente nunca.
      await resolveDepartmentId()
      if (!departmentContextInvalid.value) {
        // Pasar paginación y filtros básicos a backend
        const params: any = { page: currentPage.value, limit: itemsPerPage.value }
        if (filters.value.status?.length === 1) params.status = filters.value.status[0]
        if (filters.value.priority?.length === 1) params.priority = filters.value.priority[0]
        const deptResp = await JefeDepartamentoService.getIncidencesByDepartment(departmentId.value, params)
        // P13-V7B: a read that FAILED is said to have failed. The service answers a failure with an
        // empty list, and the page rendered that as "No se encontraron incidencias" — a refusal or a
        // broken read presented as an empty department.
        if (deptResp?.success === false && !isStale()) {
          showNotification(
            deptResp.forbidden
              ? 'El servidor no autoriza ese departamento para este usuario'
              : 'Error al cargar las incidencias',
            deptResp.forbidden ? 'warning' : 'error'
          )
        }
        departmentMeta = deptResp?.meta ?? null
        const raw: any[] = Array.isArray(deptResp?.data) ? deptResp.data : (Array.isArray(deptResp) ? deptResp : [])
        // Normalizar enums y estructura mínima esperada por la UI
        // P13-V7B (refutation): an absent or unrecognised priority stays ABSENT (null). It used to
        // become 'MEDIUM', so the row, the detail dialog and the "Media Prioridad" card all counted a
        // priority nobody set.
        const mapPriority = (p: string) => {
          const v = (p || '').toString().toUpperCase()
          const dict: Record<string, string> = { CRITICAL: 'CRITICAL', HIGH: 'HIGH', MEDIUM: 'MEDIUM', LOW: 'LOW', 'ALTA': 'HIGH', 'MEDIA': 'MEDIUM', 'BAJA': 'LOW', 'CRÍTICA': 'CRITICAL', 'CRITICA': 'CRITICAL' }
          return (dict[v] as any) || null
        }
        const mapStatus = (s: string) => {
          // P13-V1D: normalización 1:1 de los cinco estados canónicos. La coerción anterior
          // NO conocía PENDING_VALIDATION y lo colapsaba silenciosamente a 'PENDING' — un jefe de
          // departamento veía incidencias en validación como si estuvieran sin triar.
          const v = (s || '').toString().toUpperCase()
          const CANONICAL = ['PENDING', 'IN_PROGRESS', 'PENDING_VALIDATION', 'CLOSED', 'CANCELLED']
          if (CANONICAL.includes(v)) return v as any
          const legacy: Record<string, string> = { ABIERTO: 'PENDING', 'EN_PROCESO': 'IN_PROGRESS', 'EN PROCESO': 'IN_PROGRESS' }
          return (legacy[v] as any) || 'PENDING'
        }
        const enhanced = raw.map((inc: any, idx: number) => ({
          id: inc.id ?? idx + 1,
          title: inc.title || `Incidencia #${idx + 1}`,
          description: inc.description || '',
          priority: mapPriority(inc.priority) as any,
          status: mapStatus(inc.status) as any,
          // P13-V7B (refutation): the reader sends no category (the model has none), and every row
          // used to be stamped 'OTHER' — printed "Otra" in the detail dialog. Absent stays absent.
          category: inc.category ? inc.category.toString().toUpperCase() : null,
          agencyId: inc.pointId || inc.agencyId || null,
          agencyName: inc.agencyName || inc.point?.name || '',
          point: inc.point || null,
          location: inc.location || null,
          assignedTo: inc.assignedUser ? { id: String(inc.assignedUser.id), name: inc.assignedUser.name, avatar: null } : null,
          reportedBy: inc.reportedByUser ? { id: String(inc.reportedByUser.id), name: inc.reportedByUser.name, email: inc.reportedByUser.email } : null,
          createdAt: inc.createdAt || new Date().toISOString(),
          updatedAt: inc.updatedAt || new Date().toISOString(),
          attachments: inc.attachments || [],
          comments: inc.comments || [],
          statusHistory: inc.statusHistory || []
        }))
        // P13-V7B: `count` is the server's `meta.total`, or null when it stated none.
        response = { data: enhanced, total: deptResp?.count ?? null }
      } else {
        // FAIL CLOSED. Esta rama ya NO significa "no se pudo resolver" — eso ahora es una lectura
        // canónica sin estrechamiento. Significa: se suministró un departmentId y era inválido, y
        // una petición inválida no puede convertirse en una lectura más amplia.
        console.warn('⛔ departmentId suministrado e inválido; no se realiza la lectura departamental')
        // P13-V7B: no read happened, so no total is claimed either (it used to publish "0 de 0").
        response = { data: [], total: null }
      }
    } else {
      // Otros roles usan el servicio general con filtros
      // P13-V7B (refutation): the general reader is paginated too (it was read as if unpaginated, so
      // "50 de 50" was published and rows past the first page were unreachable). It is paged like the
      // department reader, by the same controls.
      response = await incidentsService.getIncidents({ ...filters.value, page: currentPage.value, limit: itemsPerPage.value })
      // P13-V7B (refutation): these rows carry `assignedTo` as the assignee's ID and the person in
      // `assignedUser`; the column reads `assignedTo.name`, so every assigned incidence was shown as
      // "Sin asignar". The person the server sent is what the column shows (as the department reader
      // above already does).
      response = {
        ...response,
        data: (response?.data || []).map((inc: any) => ({
          ...inc,
          assignedTo: inc.assignedUser
            ? { id: String(inc.assignedUser.id), name: inc.assignedUser.name, avatar: null }
            : (inc.assignedTo && typeof inc.assignedTo === 'object' ? inc.assignedTo : null),
        })),
      }
    }
    console.log('📥 AdvancedIncidentsManagement - Respuesta del servicio:', response)

    // Una carga posterior ya empezó: esta respuesta describe un alcance que ya no es el vigente.
    // Descartarla; publicarla sobrescribiría el resultado más reciente — incluido un fail-closed.
    if (isStale()) {
      console.warn('↩️ Respuesta obsoleta descartada: otra carga la adelantó')
      return
    }

    incidents.value = response.data
    // P13-V7B: the total is the SERVER's, and only the server's. Every reader behind this page is
    // paginated (the department reader, and — found by the refutation round — the general one, which
    // this comment used to call unpaginated), so the number of rows received says nothing about how
    // many exist; when no total was stated the header shows the rows on screen and claims no universe.
    totalIncidents.value = typeof response.total === 'number' && Number.isFinite(response.total) ? response.total : null

    console.log('✅ AdvancedIncidentsManagement - Datos asignados:')
    console.log('   - incidents.value.length:', incidents.value.length)
    console.log('   - totalIncidents.value:', totalIncidents.value)
    console.log('   - Primera incidencia:', incidents.value[0])
    
    // Extraer agencias únicas de las incidencias cargadas
    const uniquePoints = new Map()
    incidents.value.forEach(inc => {
      if (inc.point && inc.point.id) {
        uniquePoints.set(inc.point.id, {
          id: inc.point.id,
          name: inc.point.name,
          address: inc.point.address || '',
          city: inc.point.city || '',
          status: 'ACTIVE',
          location: {
            lat: inc.point.latitude || 0,
            lng: inc.point.longitude || 0
          }
        })
      }
    })
    
    // Actualizar agencies si encontramos points.
    // XSC-M3A · productor paralelo del MISMO catálogo contenido: para department_head esto sería un
    // catálogo PARCIAL derivado de las incidencias visibles (2 de 3 en la medición), no un catálogo
    // con autoridad. Se conserva íntegro para los roles que sí la tienen. Este bloque se re-ejecuta
    // en cada reentrada a loadIncidents (mount, refreshData, applyFilters, paginación, cambio de
    // departamento), así que la contención tiene que vivir aquí y no solo en el montaje.
    if (uniquePoints.size > 0 && agencyCatalogueSurfaceAllowed.value) {
      agencies.value = Array.from(uniquePoints.values())
      console.log('✅ Agencias extraídas de incidencias:', agencies.value.length)
    }
    
    // P13-V7B (refutation): every reader is paginated; the cards count only a complete result.
    updateStats({ paginated: true, meta: isDepartmentHead ? departmentMeta : null, total: totalIncidents.value })
  } catch (error) {
    if (isStale()) return
    console.error('❌ Error cargando incidencias:', error)
    showNotification('Error al cargar las incidencias', 'error')
  } finally {
    // Sólo la carga vigente puede apagar el indicador: si lo hiciera una obsoleta, la UI diría
    // "listo" mientras la petición que de verdad importa sigue en vuelo.
    if (!isStale()) loading.value = false
  }
}

const loadSupportData = async () => {
  try {
    // Para DepartmentHead cargar miembros del departamento en lugar de supervisores
    if (auth.userRole === 'department_head') {
      console.log('👥 DepartmentHead: Cargando miembros del departamento...')

      // XSC-M3A · no se pide catálogo de agencias para este rol.
      // `incidentsService.getAgencies()` lee GET /points, CONTENIDO para department_head (403). El
      // piloto firmado no vende ninguna autoridad de catálogo de agencias al jefe de departamento,
      // así que pedirlo solo producía una lista vacía detrás de un filtro que seguía anunciándose.
      // Los demás roles conservan su catálogo en la rama de abajo.

      // P13-V7B · no se piden los miembros del equipo para este rol.
      // Esta rama leía `JefeDepartamentoService.getTeamMembers()`, que componía
      // `GET /api/api/department-head/team-metrics` (prefijo doblado → 404) en cada montaje. No se
      // repara el prefijo: la ruta canónica `/department-head/team-metrics` está por debajo de la
      // frontera vendor-only del router del backend, así que el jefe de departamento recibiría un
      // 403. La lista resultante era siempre vacía y sólo alimentaba el panel de asignación del
      // diálogo de detalle, que ahora muestra al asignado tal como lo trae la propia incidencia. La
      // lista de ASIGNABLES es otra cosa y sigue viva: `eligible-assignees`, que sí admite este rol.
      supervisors.value = []
      return
    }

    // P13-V7B (refutation): the supervisor catalogue is no longer read here. Its two consumers were the
    // "Supervisor" filter — which never matched a super-admin row (they carry `reportedBy` as a bare
    // id) and is now offered only to the department reader — and the detail dialog's contact card,
    // which the service fills with invented defaults ('+1809-555-0000', '<usuario>@mgi.do', 'Sin
    // asignar') whenever the user record lacks them. No list is better than a list that fabricates.
    supervisors.value = []
    const agenciesResponse = await incidentsService.getAgencies()
    agencies.value = agenciesResponse.data || []
  } catch (error) {
    console.error('Error cargando datos de soporte:', error)
  }
}

/**
 * P13-V7B — THE CARDS COUNT A UNIVERSE, SO THEY NEED ONE.
 *
 * For the roles that read an unpaginated list, the loaded rows ARE the universe and the cards count
 * them, as before. The department reader is paginated: counting its rows published the priorities
 * of ONE PAGE as the priorities of the department. For that reader the figures come from the
 * statistics block the server sends beside the rows — and only when that block provably covers the
 * whole result (`meta.stats.total === meta.total`; the backend computes it over a bounded sample of
 * recent incidences and says so in its own source). When it does not, or when the rows on screen
 * are themselves the whole result, the rule is the same one: count what is complete, state nothing
 * about what is not.
 */
const statedCount = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null

const updateStats = (scope?: { paginated: boolean; meta: any; total: number | null }) => {
  const countRows = () => ({
    critical: incidents.value.filter(i => i.priority === 'CRITICAL').length,
    high: incidents.value.filter(i => i.priority === 'HIGH').length,
    medium: incidents.value.filter(i => i.priority === 'MEDIUM').length,
    // P13-V1D: la KPI "resueltas" cuenta CLOSED (RESOLUTION_ACCEPTED) — el predicado legacy
    // contaba un estado que la FSM ya no produce, así que siempre era 0.
    resolved: incidents.value.filter(i => i.status === 'CLOSED').length
  })

  if (!scope?.paginated) {
    stats.value = countRows()
    return
  }

  const serverStats = scope.meta?.stats
  const total = scope.total
  if (total !== null && serverStats && statedCount(serverStats.total) === total) {
    stats.value = {
      critical: statedCount(serverStats.critical),
      high: statedCount(serverStats.high),
      medium: statedCount(serverStats.medium),
      resolved: statedCount(serverStats.closed)
    }
    return
  }

  // The page on screen is provably the whole result: its rows can be counted.
  if (total !== null && incidents.value.length === total) {
    stats.value = countRows()
    return
  }

  stats.value = { critical: null, high: null, medium: null, resolved: null }
}

// Métodos de UI
const applyFilters = async () => {
  filtering.value = true
  // P13-V7B (refutation round 3): a new filter starts at its first page (the pager used to stay on,
  // say, page 3 of the previous result). Changing the page lets the pager's own watcher load once.
  if (currentPage.value !== 1) {
    currentPage.value = 1
  } else {
    await loadIncidents()
  }
  filtering.value = false
}

const clearFilters = () => {
  filters.value = {
    search: '',
    priority: [],
    status: [],
    category: [],
    supervisor: null,
    agency: null,
    dateFrom: '',
    dateTo: ''
  }
  applyFilters()
}

const refreshData = () => {
  loadIncidents()
}

// Métodos CRUD
const openCreateDialog = () => {
  selectedIncident.value = null
  dialogs.value.form = true
}

const viewIncident = (incident: Incident) => {
  selectedIncident.value = incident
  dialogs.value.details = true
}

const editIncident = (incident: Incident) => {
  // W2 LIFECYCLE-SIBLING: unreachable for a delivered identity even if the affordance were restored.
  if (!canUseLifecycleWrites.value) return
  // 🔒 VALIDACIÓN: Prevenir edición de incidencias cerradas
  if (incident.status === 'CLOSED' || incident.status === 'closed') {
    showNotification(
      'No se puede editar una incidencia cerrada. Si necesita hacer cambios, primero debe reabrirla.',
      'warning'
    )
    return
  }
  
  selectedIncident.value = incident
  dialogs.value.form = true
}

const deleteIncident = (incident: Incident) => {
  // W2 LIFECYCLE-SIBLING: unreachable for a delivered identity even if the affordance were restored.
  if (!canUseLifecycleWrites.value) return
  selectedIncident.value = incident
  dialogs.value.delete = true
}

const assignIncident = (incident: Incident) => {
  // P13-V7B (refutation): the affordances are withheld where assign-user refuses the session; this
  // keeps any other emitter (the card view's button, the dialog) from opening a refused flow.
  if (!canAssignHandler.value) return
  selectedIncident.value = incident
  // P13-V1C: precargar la lista ADVISORY de asignables (el servidor revalida en la transacción;
  // un 403/422 aquí solo significa que este actor no puede asignar esta incidencia).
  eligibleAssignees.value = []
  assigneesLoading.value = true
  incidentsService.getEligibleAssignees(incident.id)
    .then((list) => { eligibleAssignees.value = list })
    .catch((err: any) => {
      showNotification(err?.response?.data?.error || 'No hay asignables disponibles para esta incidencia', 'warning')
    })
    .finally(() => { assigneesLoading.value = false })
  dialogs.value.assign = true
}

const addComment = (incident: Incident) => {
  selectedIncident.value = incident
  dialogs.value.comments = true
}

const showStatusHistory = async (incident: Incident) => {
  try {
    // Cargar detalles completos de la incidencia para obtener el historial
    const response = await incidentsService.getIncidentById(incident.id)
    selectedIncident.value = response
    dialogs.value.history = true
  } catch (error) {
    console.error('Error cargando detalles de incidencia:', error)
    showNotification('Error al cargar el historial de la incidencia', 'error')
  }
}

// Handlers de eventos
const handleSaveIncident = async (incidentData: CreateIncidentRequest | UpdateIncidentRequest) => {
  try {
    // W2 LIFECYCLE-SIBLING: only the UPDATE branch is contained. Creation stays signed, so the guard
    // sits inside the branch that would issue PUT /incidences/:id, never around the whole handler.
    if (selectedIncident.value && !canUseLifecycleWrites.value) return
    if (selectedIncident.value) {
      // P13-V6B@V1 DECISION-04 — el PUT genérico es SOLO metadata no autoritativa. El estado y las
      // asignaciones cambian únicamente por sus operaciones nombradas.
      //
      // El cuerpo se construye como un LITERAL EXPLÍCITO, no quitando campos conocidos de lo que
      // llegue: una lista de exclusión sólo conoce lo que ya existía, así que un campo nuevo en el
      // formulario —o un emisor distinto de este diálogo— viajaba solo hasta el servidor y rompía la
      // edición entera con un 400 `UPDATE_FIELD_NOT_ALLOWED`. Enumerar la allowlist invierte la
      // regla: lo que no está nombrado aquí no puede salir. La garantía es de EJECUCIÓN, no de
      // compilación —este repositorio no tiene comprobación semántica efectiva—, y por eso está
      // cubierta por una lane que envía un formulario hostil y compara las claves exactas del PUT.
      const updateData: UpdateIncidentRequest = {
        title: incidentData.title,
        description: incidentData.description,
        priority: incidentData.priority,
      }
      // Actualizar incidencia existente
      const updatedIncident = await incidentsService.updateIncident(selectedIncident.value.id, updateData)
      
      // Actualización optimista: actualizar en la lista local
      const index = incidents.value.findIndex(inc => inc.id === selectedIncident.value!.id)
      if (index !== -1) {
        // Mantener campos que no vienen en la respuesta
        incidents.value[index] = {
          ...incidents.value[index],
          ...updatedIncident,
          // Normalizar a UPPERCASE para consistencia con el frontend
          priority: (updatedIncident.priority?.toUpperCase() || incidents.value[index].priority) as any,
          status: (updatedIncident.status?.toUpperCase() || incidents.value[index].status) as any,
          category: (updatedIncident.category?.toUpperCase() || incidents.value[index].category) as any
        }
        // Forzar reactividad
        incidents.value = [...incidents.value]
      }
      
      showNotification('Incidencia actualizada correctamente', 'success')
    } else {
      // Crear nueva incidencia
      await incidentsService.createIncident(incidentData as CreateIncidentRequest)
      showNotification('Incidencia creada correctamente', 'success')
      await loadIncidents()
    }
    
    dialogs.value.form = false
    selectedIncident.value = null
  } catch (error: any) {
    console.error('Error guardando incidencia:', error)
    
    // Extraer mensaje de error específico del servidor
    let errorMessage = 'Error al guardar la incidencia'
    
    if (error?.response?.data) {
      const responseData = error.response.data
      
      // Caso 1: Error de validación con detalles
      if (responseData.details && Array.isArray(responseData.details)) {
        const validationErrors = responseData.details
          .map((detail: any) => detail.message || detail.field)
          .join(', ')
        errorMessage = `Error de validación: ${validationErrors}`
      }
      // Caso 2: Mensaje de error directo
      else if (responseData.message) {
        errorMessage = responseData.message
      }
      // Caso 3: Error genérico con descripción
      else if (responseData.error) {
        errorMessage = typeof responseData.error === 'string' 
          ? responseData.error 
          : 'Error al procesar la solicitud'
      }
    }
    // Caso 4: Error de red o timeout
    else if (error?.message) {
      errorMessage = `Error: ${error.message}`
    }
    
    showNotification(errorMessage, 'error')
  }
}

const handleCancelForm = () => {
  dialogs.value.form = false
  selectedIncident.value = null
}

const handleCloseDetails = () => {
  dialogs.value.details = false
  selectedIncident.value = null
}

const handleCancelAssign = () => {
  dialogs.value.assign = false
  selectedIncident.value = null
}

const handleCloseComments = () => {
  dialogs.value.comments = false
  // NO resetear selectedIncident porque el usuario aún puede estar viendo el dialog de detalles
}

const handleCancelDelete = () => {
  dialogs.value.delete = false
  selectedIncident.value = null
}

const handleCloseHistory = () => {
  dialogs.value.history = false
  // NO resetear selectedIncident porque el usuario aún puede estar viendo el dialog de detalles
}

const handleAssignIncident = async (targetUserId: number) => {
  // P13-V1C — assign-user canónico. Sin escritura optimista: el estado visible llega por refetch
  // canónico (y por la señal incident:handler-changed). Un 409/403 del servidor es la autoridad.
  try {
    await incidentsService.assignIncidentUser(selectedIncident.value!.id, targetUserId)
    showNotification('Handler asignado correctamente', 'success')
    dialogs.value.assign = false
    selectedIncident.value = null
    await loadIncidents()
  } catch (error: any) {
    const code = error?.response?.data?.code
    const msg = error?.response?.data?.error
      || (code === 'INCIDENT_ALREADY_CLAIMED' ? 'La incidencia ya tiene un handler activo' : 'Error al asignar el handler')
    console.error('Error asignando handler:', error)
    showNotification(msg, 'error')
  }
}

const handleAddComment = async (comment: string) => {
  try {
    // El CommentsDialog ya hizo el POST, aquí solo recargamos
    showNotification('Comentario agregado correctamente', 'success')
    
    // Recargar comentarios en el diálogo de detalles si está abierto
    if (detailsDialogRef.value && dialogs.value.details) {
      await detailsDialogRef.value.loadComments()
    }
    
    // Recargar lista de incidencias para actualizar contador
    await loadIncidents()
  } catch (error) {
    console.error('Error recargando comentarios:', error)
    showNotification('Error al recargar comentarios', 'error')
  }
}

const handleDeleteConfirm = async () => {
  // W2 LIFECYCLE-SIBLING: the confirm path is the ONLY dispatcher of DELETE /incidences/:id (single
  // and bulk). Guarded independently of `deleteIncident`, because a restored affordance is not the
  // only way to reach it — the dialog can be driven directly.
  if (!canUseLifecycleWrites.value) return
  // El PADRE es dueño del ciclo de vida de la petición: es el único que sabe cuándo empieza y cuándo
  // termina. El diálogo derivaba su estado de carga de una variable interna que sólo se liberaba al
  // reabrirlo, y como el 409 canónico CONSERVA el diálogo abierto, el spinner quedaba encendido para
  // siempre y «Cerrar» deshabilitado: la política se comunicaba y la interfaz quedaba muerta.
  if (deleteRequestPending.value) return
  deleteRequestPending.value = true
  try {
    if (selectedIncidents.value.length > 1) {
      // Eliminación en lote
      await Promise.all(
        selectedIncidents.value.map(incident =>
          incidentsService.deleteIncident(incident.id)
        )
      )
      showNotification(`${selectedIncidents.value.length} incidencias eliminadas`, 'success')
      selectedIncidents.value = []
    } else {
      // Eliminación individual
      await incidentsService.deleteIncident(selectedIncident.value!.id)
      showNotification('Incidencia eliminada correctamente', 'success')
    }
    
    // REQUEST_COMPLETED en cuanto la eliminación responde: la reconciliación de la lista que viene
    // a continuación ya no es «la solicitud en vuelo», y mantener el estado ocupado durante ella
    // habría dejado ocupado un diálogo reabierto para OTRA incidencia mientras recarga.
    deleteRequestPending.value = false
    dialogs.value.delete = false
    selectedIncident.value = null
    await loadIncidents()
  } catch (error: any) {
    // La eliminación falla cerrada por diseño: un 409 INCIDENCE_DELETE_BLOCKED_BY_DEPENDENCIES es una
    // respuesta de política, no un fallo transitorio. Se comunica con las palabras del servidor, se
    // conservan la fila y el diálogo, y no se invita a reintentar algo que no puede tener éxito.
    const status = error?.response?.status
    const code = error?.response?.data?.code
    if (status === 409 && code === 'INCIDENCE_DELETE_BLOCKED_BY_DEPENDENCIES') {
      showNotification(
        error?.response?.data?.error
          || 'La incidencia conserva historial y dependencias operativas; no puede eliminarse.',
        'warning',
      )
      return
    }
    console.error('Error eliminando incidencia:', error)
    showNotification('Error al eliminar la incidencia', 'error')
  } finally {
    // Red de seguridad de REQUEST_COMPLETED: cubre las salidas por política y por fallo genérico —
    // incluido el `return` de la rama de 409— y es idempotente con la liberación de la rama de éxito.
    // El diálogo vuelve a ser usable aunque siga abierto, que es justo lo que exige la política.
    deleteRequestPending.value = false
  }
}

const handleStatusChange = async (newStatus: string) => {
  try {
    const incidentId = selectedIncident.value!.id
    // P13-V1D (frozen consumer contract): SERVER-CONFIRMED state only. The prior handler wrote the
    // requested status into the local list/dialog and explicitly REFUSED to reload — a denied
    // transition (403/409) left the UI showing a state the server never accepted. The canonical
    // refetch is the only materialization path; no optimistic authority.
    await incidentsService.updateStatus(incidentId, newStatus.toLowerCase())
    showNotification('Estado actualizado correctamente', 'success')
    await loadIncidents()
    if (selectedIncident.value && selectedIncident.value.id === incidentId) {
      const fresh = incidents.value.find(inc => inc.id === incidentId)
      if (fresh) selectedIncident.value = { ...selectedIncident.value, ...fresh }
    }
  } catch (error) {
    console.error('Error cambiando estado:', error)
    showNotification('Error al cambiar el estado', 'error')
    // Refetch restaura la verdad del servidor también en el camino de error.
    await loadIncidents()
  }
}

// 🔄 Handler para actualizaciones de incidencia desde IncidentDetailsDialog (WebSocket)
const handleIncidentUpdated = (data: any) => {
  console.log('📡 AdvancedIncidentsManagement: Recibido incident-updated desde IncidentDetailsDialog:', data)
  
  // Actualizar la incidencia en la lista local
  const incidentIndex = incidents.value.findIndex(inc => inc.id === data.id)
  if (incidentIndex !== -1) {
    incidents.value[incidentIndex].status = data.status.toUpperCase() as any
    incidents.value[incidentIndex].updatedAt = data.updatedAt
    
    // Forzar reactividad
    incidents.value = [...incidents.value]
    
    console.log('✅ Incidencia actualizada en lista principal desde WebSocket')
  }
  
  // También actualizar selectedIncident si es la misma
  if (selectedIncident.value && selectedIncident.value.id === data.id) {
    selectedIncident.value.status = data.status.toUpperCase() as any
    selectedIncident.value.updatedAt = data.updatedAt
    console.log('✅ selectedIncident actualizado desde WebSocket')
  }
}

// ═══════════════════════════════════════════════════════════════════════════════════════════
// P13-V6B@V1 — consumo de señales realtime del dominio. Regla: el payload es SEÑAL, no estado —
// toda actualización visible proviene de un refetch canónico server-scoped; un subject inválido
// es no-op; `incident:access-revoked` SOLO remueve/cierra (sin refetch del subject, sin error, y
// un frame viejo posterior no lo rematerializa porque las listas solo se pueblan desde el server).
// ═══════════════════════════════════════════════════════════════════════════════════════════
const subjectIdOf = (data: any): number | null => {
  const n = Number(data?.incidenceId)
  return Number.isInteger(n) && n > 0 ? n : null
}

// P13-V1-V6B REPAIR-04 — trailing-edge coalescing. The previous guard returned early while a
// refetch was in flight, which silently discarded the LAST signal of a burst and could leave the
// table showing a state older than the server's. Signals arriving mid-flight now mark the result
// dirty and force one more canonical read once it settles.
const refetchListCoalesced = useTrailingRefetch(() => loadIncidents())

const handleRealtimeIncidentCreated = async (data: any) => {
  if (subjectIdOf(data) === null) return
  await refetchListCoalesced()
}

const handleRealtimeDepartmentAssigned = async (data: any) => {
  const id = subjectIdOf(data)
  if (id === null) return
  await refetchListCoalesced()
  // Si el detalle abierto es la incidencia reasignada, reconciliarlo desde verdad persistida.
  if (selectedIncident.value && Number(selectedIncident.value.id) === id) {
    try {
      selectedIncident.value = await incidentsService.getIncidentById(id)
    } catch {
      // 403/404: el actor perdió acceso — tratar como invalidación local.
      handleRealtimeAccessRevoked({ incidenceId: id })
    }
  }
}

// P13-V1C — señal de autoridad del claim atómico (CLAIMED | ASSIGNED | RELEASED). Igual contrato
// que las demás señales: el payload jamás se materializa; la lista y el detalle abierto se
// reconcilian desde la verdad persistida (availableActions incluidos — los botones de claim/release
// provienen del servidor, nunca de un cálculo local).
const handleRealtimeHandlerChanged = async (data: any) => {
  const id = subjectIdOf(data)
  if (id === null) return
  await refetchListCoalesced()
  if (selectedIncident.value && Number(selectedIncident.value.id) === id) {
    try {
      selectedIncident.value = await incidentsService.getIncidentById(id, { cacheBuster: Date.now() })
    } catch {
      handleRealtimeAccessRevoked({ incidenceId: id })
    }
  }
}

const handleRealtimeStatusChanged = async (data: any) => {
  const id = subjectIdOf(data)
  if (id === null) return
  // La transición de estado es visible en la LISTA, no solo en el diálogo: refetch canónico
  // (el payload nunca se materializa en la fila; la verdad viene del endpoint server-scoped).
  await refetchListCoalesced()
  if (selectedIncident.value && Number(selectedIncident.value.id) === id) {
    try {
      selectedIncident.value = await incidentsService.getIncidentById(id, { cacheBuster: Date.now() })
    } catch {
      handleRealtimeAccessRevoked({ incidenceId: id })
    }
  }
}

const handleRealtimeAccessRevoked = (data: any) => {
  const id = subjectIdOf(data)
  if (id === null) return
  incidents.value = incidents.value.filter(inc => Number(inc.id) !== id)
  if (selectedIncident.value && Number(selectedIncident.value.id) === id) {
    selectedIncident.value = null
    dialogs.value.details = false
    dialogs.value.comments = false
    dialogs.value.history = false
    dialogs.value.assign = false
    dialogs.value.form = false
  }
  showNotification(`Ya no tienes acceso a la incidencia #${id}`, 'info')
}

const handleRealtimeReconnect = async () => {
  // Resincronización de la superficie incluida tras reconexión (sin replay: verdad = server).
  await refetchListCoalesced()
}

onMounted(() => {
  websocketService.on(RT_LOCAL.INCIDENT_CREATED, handleRealtimeIncidentCreated)
  websocketService.on(RT_LOCAL.INCIDENT_DEPARTMENT_ASSIGNED, handleRealtimeDepartmentAssigned)
  websocketService.on(RT_LOCAL.INCIDENT_HANDLER_CHANGED, handleRealtimeHandlerChanged)
  websocketService.on(RT_LOCAL.INCIDENT_STATUS_CHANGED, handleRealtimeStatusChanged)
  websocketService.on(RT_LOCAL.INCIDENT_ACCESS_REVOKED, handleRealtimeAccessRevoked)
  websocketService.on(RT_LOCAL.SOCKET_RECONNECT, handleRealtimeReconnect)
})

onUnmounted(() => {
  websocketService.off(RT_LOCAL.INCIDENT_CREATED, handleRealtimeIncidentCreated)
  websocketService.off(RT_LOCAL.INCIDENT_DEPARTMENT_ASSIGNED, handleRealtimeDepartmentAssigned)
  websocketService.off(RT_LOCAL.INCIDENT_HANDLER_CHANGED, handleRealtimeHandlerChanged)
  websocketService.off(RT_LOCAL.INCIDENT_STATUS_CHANGED, handleRealtimeStatusChanged)
  websocketService.off(RT_LOCAL.INCIDENT_ACCESS_REVOKED, handleRealtimeAccessRevoked)
  websocketService.off(RT_LOCAL.SOCKET_RECONNECT, handleRealtimeReconnect)
})

// Métodos de acciones en lote
// P13-V7B (refutation): `bulkAssign` and `bulkChangeStatus` are RETIRED with their buttons — their
// whole body was a toast saying the function was "en desarrollo".
const bulkDelete = () => {
  if (selectedIncidents.value.length === 0) return
  dialogs.value.delete = true
}

// Métodos de navegación
const viewOnMap = () => {
  // P13-V7B: the affordance is already withheld where the route refuses the session; this keeps the
  // handler from navigating into a refusal if it is ever reached another way.
  if (!admits('/maps')) return
  router.push('/maps')
}

// Métodos auxiliares
const getPriorityColor = (priority: string) => {
  const colors: Record<string, string> = {
    CRITICAL: 'error',
    HIGH: 'warning',
    MEDIUM: 'info',
    LOW: 'success'
  }
  return colors[priority] || 'grey'
}

const getPriorityIcon = (priority: string) => {
  const icons: Record<string, string> = {
    CRITICAL: 'mdi-alert-circle',
    HIGH: 'mdi-alert',
    MEDIUM: 'mdi-information',
    LOW: 'mdi-check-circle'
  }
  return icons[priority] || 'mdi-help'
}

// P13-V1D (frozen decision #1): los cinco estados canónicos con color y etiqueta — antes
// PENDING_VALIDATION caía al color genérico y se renderizaba como el literal crudo del enum.
const getStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    PENDING: 'warning',
    IN_PROGRESS: 'info',
    PENDING_VALIDATION: 'purple',
    CLOSED: 'success',
    CANCELLED: 'error'
  }
  return colors[status] || 'grey'
}

const getStatusText = (status: string) => {
  if (!status) return ''

  // Normalizar a UPPERCASE para buscar en el diccionario
  const normalizedStatus = status.toString().toUpperCase()

  const texts: Record<string, string> = {
    PENDING: 'Pendiente',
    IN_PROGRESS: 'En Proceso',
    PENDING_VALIDATION: 'Pendiente Validación',
    CLOSED: 'Cerrado',
    CANCELLED: 'Cancelado'
  }

  return texts[normalizedStatus] || status
}

const getPriorityText = (priority: string) => {
  if (!priority) return ''
  
  const normalizedPriority = priority.toString().toUpperCase()
  
  const texts: Record<string, string> = {
    LOW: 'Baja',
    MEDIUM: 'Media',
    HIGH: 'Alta',
    CRITICAL: 'Crítica'
  }
  
  return texts[normalizedPriority] || priority
}

const getCategoryText = (category: string) => {
  if (!category) return ''
  
  const normalizedCategory = category.toString().toUpperCase()
  
  const texts: Record<string, string> = {
    PERSONAL: 'Personal',
    TECHNICAL: 'Técnico',
    INFRASTRUCTURE: 'Infraestructura',
    ELECTRICAL: 'Eléctrico',
    INTERNET: 'Internet',
    EQUIPMENT: 'Equipos',
    OTHER: 'Otro'
  }
  
  return texts[normalizedCategory] || category
}

const getCategoryColor = (category: string) => {
  const colors: Record<string, string> = {
    PERSONAL: 'purple',
    TECHNICAL: 'blue',
    INFRASTRUCTURE: 'orange',
    ELECTRICAL: 'yellow',
    INTERNET: 'green',
    EQUIPMENT: 'teal',
    OTHER: 'grey'
  }
  return colors[category] || 'grey'
}

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString('es-DO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getTimeAgo = (date: string) => {
  const now = new Date()
  const past = new Date(date)
  const diffMs = now.getTime() - past.getTime()
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffHours / 24)
  
  if (diffDays > 0) {
    return `hace ${diffDays} día${diffDays > 1 ? 's' : ''}`
  } else if (diffHours > 0) {
    return `hace ${diffHours} hora${diffHours > 1 ? 's' : ''}`
  } else {
    return 'hace menos de una hora'
  }
}

// Debug functions (comentadas - disponibles si se necesitan)
/*
const debugTable = () => {
  console.log('🐛 DEBUG TABLE:')
  console.log('📊 incidents.value:', incidents.value)
  console.log('📊 filteredIncidents.value:', filteredIncidents.value)
  console.log('📊 tableHeaders:', tableHeaders)
  console.log('📊 viewMode:', viewMode.value)
  console.log('📊 loading:', loading.value)
  console.log('📊 selectedIncidents:', selectedIncidents.value)
  
  // Verificar estructura de la primera incidencia
  if (filteredIncidents.value.length > 0) {
    const firstItem = filteredIncidents.value[0]
    console.log('🔍 Primera incidencia completa:', firstItem)
    console.log('🔍 Campos esperados por headers:')
    tableHeaders.forEach(header => {
      console.log(`   - ${header.key}:`, firstItem[header.key])
    })
  }
}

const forceTableVisible = () => {
  console.log('👁️ FORZANDO VISIBILIDAD DE TABLA')
  
  // Forzar viewMode a table
  viewMode.value = 'table'
  
  // Forzar re-render
  forceRender.value++
  
  // Scroll hasta la tabla
  setTimeout(() => {
    const tableElement = document.querySelector('.incidents-simple-table')
    const tableCard = document.querySelector('.incidents-table-card')
    const alertElement = document.querySelector('.v-alert')
    
    console.log('🔍 Elementos encontrados:')
    console.log('   - Table:', !!tableElement)
    console.log('   - Card:', !!tableCard)
    console.log('   - Alert:', !!alertElement)
    
    if (tableElement) {
      console.log('✅ Tabla encontrada, haciendo scroll')
      tableElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
      
      // Agregar un borde temporal para verificar visibilidad
      tableElement.style.border = '5px solid red'
      tableElement.style.minHeight = '400px'
      tableElement.style.background = 'yellow'
      setTimeout(() => {
        tableElement.style.border = ''
        tableElement.style.background = ''
      }, 5000)
    } else {
      console.log('❌ Tabla NO encontrada en DOM')
    }
    
    if (alertElement) {
      console.log('✅ Alert encontrada')
      alertElement.style.fontSize = '18px'
      alertElement.style.background = 'lime'
    }
    
    if (tableCard) {
      console.log('✅ Card de tabla encontrada')
      tableCard.style.minHeight = '800px'
      tableCard.style.backgroundColor = '#ffe0e0'
      tableCard.style.border = '3px solid purple'
    }
  }, 200)
}

const createEmergencyTable = () => {
  console.log('🚨 CREANDO TABLA DE EMERGENCIA')
  
  // Encontrar el contenedor de la tabla
  const tableCard = document.querySelector('.incidents-table-card')
  
  if (tableCard) {
    // Crear HTML de tabla de emergencia
    const emergencyHTML = \`
      <div style="background: red; color: white; padding: 20px; margin: 20px; border-radius: 10px;">
        <h2>🚨 TABLA DE EMERGENCIA ACTIVADA</h2>
        <p>Datos de incidencias: \${filteredIncidents.value.length} elementos</p>
        
        <table style="width: 100%; background: white; color: black; border-collapse: collapse; margin-top: 20px;">
          <thead>
            <tr style="background: #333; color: white;">
              <th style="padding: 10px; border: 1px solid #ddd;">ID</th>
              <th style="padding: 10px; border: 1px solid #ddd;">Título</th>
              <th style="padding: 10px; border: 1px solid #ddd;">Prioridad</th>
              <th style="padding: 10px; border: 1px solid #ddd;">Estado</th>
            </tr>
          </thead>
          <tbody>
            \${filteredIncidents.value.map(incident => \`
              <tr style="border-bottom: 1px solid #ddd;">
                <td style="padding: 10px; border: 1px solid #ddd;">\${incident.id}</td>
                <td style="padding: 10px; border: 1px solid #ddd;"><strong>\${incident.title}</strong></td>
                <td style="padding: 10px; border: 1px solid #ddd;">
                  <span style="background: orange; color: white; padding: 2px 8px; border-radius: 4px;">
                    \${incident.priority}
                  </span>
                </td>
                <td style="padding: 10px; border: 1px solid #ddd;">
                  <span style="background: blue; color: white; padding: 2px 8px; border-radius: 4px;">
                    \${incident.status}
                  </span>
                </td>
              </tr>
            \`).join('')}
          </tbody>
        </table>
      </div>
    \`
    
    // Insertar la tabla de emergencia
    tableCard.innerHTML = emergencyHTML
    
    // Scroll a la tabla
    tableCard.scrollIntoView({ behavior: 'smooth', block: 'center' })
    
    console.log('✅ Tabla de emergencia creada')
  } else {
    console.log('❌ No se encontró el contenedor')
  }
}
*/

// Lifecycle
onMounted(async () => {
  // Pre-resolver departmentId si aplica
  if (auth.userRole === 'department_head') {
    await resolveDepartmentId()
    // P13-V7B · el catálogo de "departamentos accesibles" ya no se pide al montar.
    // Se leía a través de un store cuyo único productor componía
    // `GET /api/api/department-head/departments` (404 en cada carga), y cuya ruta canónica está
    // contenida a vendor-only en el backend: con el prefijo corregido el jefe recibiría un 403. El
    // selector multi-departamento depende de esa lista y por tanto nunca llegó a mostrarse; sigue
    // sin mostrarse y no promete nada. El estrechamiento explícito por `?departmentId=` no cambia,
    // y el alcance sigue siendo del servidor.
    // Si no se ha seleccionado y existe uno que coincide con departmentId actual, setear
    if (departmentId.value && !selectedDepartmentId.value) {
      selectedDepartmentId.value = departmentId.value
    }
  }
  await Promise.all([ loadIncidents(), loadSupportData() ])
})

// Re-fetch al cambiar paginación — todos los lectores están paginados (P13-V7B refutation)
watch([currentPage, itemsPerPage], async () => {
  await loadIncidents()
})

// LA NAVEGACIÓN POR SÍ SOLA TIENE QUE RELEER EL ALCANCE.
//
// El root monta un `<router-view />` sin `key`, así que cambiar sólo la query no remonta este
// componente, y no había ningún watcher de ruta: tras un `replace`/`push`/`back` que cambiara
// `departmentId`, la tabla seguía mostrando las filas del estrechamiento ANTERIOR hasta que el
// usuario refrescara, paginara o filtrara. La URL decía una cosa y la tabla mostraba otra —
// incluso cuando la URL era una petición que este mismo componente rechaza (`?departmentId=abc`).
// Que la próxima CARGA use la query vigente no basta: la navegación es la que debe provocarla.
watch(
  () => route.query.departmentId,
  async (next, prev) => {
    if (auth.userRole !== 'department_head') return
    if (next === prev) return
    await loadIncidents()
  }
)

// Cambio manual de departamento
const handleDepartmentChange = async (newVal: number | null) => {
  if (auth.userRole !== 'department_head') return
  if (!newVal) return
  // La QUERY es la autoridad de estrechamiento, así que la selección se publica ahí PRIMERO y se
  // ESPERA a que la navegación termine. Con un `router.replace` sin esperar, la lectura siguiente
  // podía resolver contra la query anterior y ganar la carrera a la propia selección del usuario.
  // El orden importa: reiniciar la paginación ANTES disparaba el watcher de `currentPage` durante la
  // navegación, lanzando una carga extra que aún leía la query vieja.
  await router.replace({ query: { ...route.query, departmentId: String(newVal) } }).catch(() => undefined)
  currentPage.value = 1
  // No se asigna `departmentId` a mano: `loadIncidents` lo deriva de la ruta recién publicada, de
  // modo que existe UNA sola fuente de estrechamiento y no dos que puedan discrepar.
  await loadIncidents()
}

// Watchers
watch(
  () => filters.value.search,
  () => {
    // Auto-filtrar mientras el usuario escribe (debounced)
    // Implementar debounce si es necesario
  }
)
</script>

<style scoped>
.advanced-incidents-management {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow-y: auto; /* Cambiar a scroll vertical */
}

/* Header */
.incidents-header {
  background: linear-gradient(135deg, #d32f2f 0%, #c62828 100%);
  padding: 2rem;
  color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
}

.header-left {
  display: flex;
  align-items: center;
}

.incidents-title {
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
  line-height: 1.2;
}

.incidents-subtitle {
  font-size: 1.1rem;
  opacity: 0.9;
  margin: 0.5rem 0 0 0;
  font-weight: 400;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

/* Dashboard de estadísticas */
.stats-dashboard {
  padding: 2rem;
  background: #f8f9fa;
}

.stat-card {
  border-radius: 16px !important;
  overflow: hidden;
  background: linear-gradient(135deg, var(--gradient-start), var(--gradient-end));
  color: white;
}

.critical-card {
  --gradient-start: #f44336;
  --gradient-end: #d32f2f;
}

.warning-card {
  --gradient-start: #ff9800;
  --gradient-end: #f57400;
}

.info-card {
  --gradient-start: #2196f3;
  --gradient-end: #1976d2;
}

.success-card {
  --gradient-start: #4caf50;
  --gradient-end: #388e3c;
}

.stat-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.stat-icon {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-numbers {
  flex: 1;
}

.stat-value {
  font-size: 2.5rem;
  font-weight: 800;
  line-height: 1;
  margin-bottom: 0.25rem;
}

.stat-label {
  font-size: 0.875rem;
  opacity: 0.9;
  font-weight: 500;
  margin-bottom: 0.5rem;
}

.stat-trend {
  font-size: 0.75rem;
  opacity: 0.8;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

/* Panel de filtros */
.filters-panel {
  margin: 0 2rem 2rem 2rem;
  border-radius: 12px !important;
}

.filter-header {
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
}

/* Tabla de incidencias */
.incidents-table-card {
  margin: 0 2rem 2rem 2rem;
  border-radius: 12px !important;
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.table-header {
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
}

.table-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.incidents-table {
  flex: 1;
}

.actions-cell {
  display: flex;
  gap: 0.25rem;
}

.bulk-actions {
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
}

/* Vista de tarjetas */
.cards-view {
  flex: 1;
  overflow-y: auto;
}

/* Responsive */
@media (max-width: 1200px) {
  .incidents-header {
    padding: 1.5rem;
  }
  
  .incidents-title {
    font-size: 1.6rem;
  }
  
  .incidents-subtitle {
    font-size: 1rem;
  }
  
  .stats-dashboard {
    padding: 1.5rem;
  }
}

@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    gap: 1.5rem;
    text-align: center;
  }
  
  .header-actions {
    flex-direction: column;
    width: 100%;
  }
  
  .stats-dashboard {
    padding: 1rem;
  }
  
  .stat-content {
    flex-direction: column;
    text-align: center;
  }
  
  .filters-panel {
    margin: 0 1rem 1rem 1rem;
  }
  
  .incidents-table-card {
    margin: 0 1rem 1rem 1rem;
  }
}

/* Animaciones */
.stat-card {
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.v-chip {
  transition: all 0.2s ease;
}

/* Estilos para la tabla simple funcional */
.incidents-simple-table {
  border-radius: 8px;
  overflow: visible !important;
  min-height: 400px !important;
  background: white !important;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1) !important;
}

.incidents-simple-table .hover-row:hover {
  background-color: #f5f5f5;
}

.incidents-simple-table td {
  padding: 12px 16px;
  vertical-align: middle;
  border-bottom: 1px solid #e0e0e0;
}

.incidents-simple-table th {
  background-color: #fafafa;
  font-weight: 600;
  padding: 16px;
  border-bottom: 2px solid #e0e0e0;
}

.actions-cell {
  display: flex;
  gap: 4px;
}

/* Asegurar que el contenedor de la tabla sea visible */
.incidents-table-card {
  margin: 0 2rem 2rem 2rem;
  border-radius: 12px !important;
  min-height: 600px !important; /* Altura mínima garantizada */
  overflow: visible !important; /* Cambiar de hidden a visible */
  display: flex;
  flex-direction: column;
}

/* Asegurar que el div de la tabla tenga altura */
.incidents-table-card .pa-4 {
  min-height: 500px !important;
  overflow: visible !important;
}

/* Estados específicos */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}
</style>
